import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGalleryImages } from "../store/slices/gallerySlice";
import { GALLERY_CATEGORIES } from "../utils/constants";

const Gallery = () => {
  const dispatch = useDispatch();
  const { images, loading } = useSelector((state) => state.gallery);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    dispatch(fetchGalleryImages());
  }, [dispatch]);

  const filtered =
    activeCategory === "All"
      ? images
      : images.filter((img) => img.category === activeCategory);

  return (
    <>
      {/* ── Page Hero ── */}
      <section className="relative h-[480px] overflow-hidden">
        <img
          src="/images/banner.png"
          alt="Our Gallery"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <span className="inline-block bg-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-white mb-5">
              Om Maruticreation
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Our Gallery
            </h1>
          </div>
        </div>
      </section>

      {/* ── Gallery Grid ── */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              Visual Showcase
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Manufacturing &amp; Products
            </h2>
            <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto">
              Explore our garment production facilities, premium men wear
              collections, and advanced manufacturing processes.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setActiveCategory("All")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeCategory === "All"
                  ? "bg-blue-700 text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-blue-400"
              }`}
            >
              All
            </button>
            {GALLERY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-blue-700 text-white shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-blue-400"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="h-10 w-10 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!loading && (!filtered || filtered.length === 0) && (
            <div className="text-center py-24">
              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 text-2xl">
                &#128247;
              </div>
              <p className="text-gray-500 text-lg">No images yet in this category.</p>
            </div>
          )}

          {/* Image Grid */}
          {!loading && filtered && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((image) => (
                <div
                  key={image._id}
                  className="relative overflow-hidden rounded-2xl group cursor-pointer"
                >
                  <div className="h-64 overflow-hidden">
                    <img
                      src={image.imageUrl}
                      alt={image.title || "Gallery"}
                      className="object-cover w-full h-full group-hover:scale-110 transition duration-500"
                    />
                  </div>
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition duration-300">
                    <span className="text-blue-400 text-xs font-semibold uppercase tracking-widest">
                      {image.category || "Gallery"}
                    </span>
                    <h3 className="text-white font-bold mt-1">
                      {image.title || "Premium Men Wear"}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Gallery;
